import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import axios from 'axios'; // <-- On va poster directement le FormData via axios
import { Voyageurs } from '../models/Voyageurs';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import FileDropZone from '../components/commun/FileDropZone';
import { API_BASE_URL } from '../config/api';
import { isLegrandUser } from '../utils/userUtils';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
}

const FormInde: React.FC = () => {
  // Récupération des paramètres passés en state via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États initiaux (issus des paramètres de route ou valeurs par défaut)
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateArriveeInde, setDateArriveeInde] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFeesForLegrand] = useState<boolean>(isLegrandUser());

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // États pour d'autres champs spécifiques
  const [lieuArrivee, setLieuArrivee] = useState<string>('');
  const [nomEmployeur, setNomEmployeur] = useState<string>('');
  const [adresseEmployeur, setAdresseEmployeur] = useState<string>('');
  const [codePostalEmployeur, setCodePostalEmployeur] = useState<string>('');
  const [villeEmployeur, setVilleEmployeur] = useState<string>('');
  const [telephoneEmployeur, setTelephoneEmployeur] = useState<string>('');
  const [nomSteInde, setNomSteInde] = useState<string>('');
  const [adresseSteInde, setAdresseSteInde] = useState<string>('');
  const [codePostalSteInde, setCodePostalSteInde] = useState<string>('');
  const [villeSteInde, setVilleSteInde] = useState<string>('');
  const [telephoneSteInde, setTelephoneSteInde] = useState<string>('');
  const [nomPrenomContactUrgence, setNomPrenomContactUrgence] = useState<string>('');
  const [adresseContact, setAdresseContact] = useState<string>('');
  const [codePostalContact, setCodePostalContact] = useState<string>('');
  const [villeContact, setVilleContact] = useState<string>('');
  const [telephoneContact, setTelephoneContact] = useState<string>('');
  const [nomHotel, setNomHotel] = useState<string>('');
  const [adresseHotel, setAdresseHotel] = useState<string>('');

  // Pour la lettre d'invitation (fichier) : l'aperçu est géré par FileDropZone
  const [letterInvitationFile, setLetterInvitationFile] = useState<File | null>(null);

  // Calcul du total
  const total =
    prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    // Contrôles de dates
    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push("La date de départ doit être une date valide et postérieure à aujourd'hui.");
      }
    } else {
      errors.push("La date de départ est requise.");
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push("La date de retour doit être une date valide et postérieure à la date de départ.");
      }
    } else if (dateSortie && !sortieDate) {
      errors.push("La date de retour est requise et doit être au format valide.");
    }

    // Champs obligatoires
    if (!selectedVoyageur) errors.push('Voyageur non sélectionné');
    if (!dateDepart.trim()) errors.push('Date de départ');
    if (!dateSortie.trim()) errors.push('Date de sortie');
    if (!lieuArrivee.trim()) errors.push("Lieu d'arrivée");
    if (!nomEmployeur.trim()) errors.push("Nom de l'employeur");
    if (!adresseEmployeur.trim()) errors.push("Adresse de l'employeur");
    if (!codePostalEmployeur.trim()) errors.push("Code postal de l'employeur");
    if (!villeEmployeur.trim()) errors.push("Ville de l'employeur");
    if (!telephoneEmployeur.trim()) errors.push("Téléphone de l'employeur");

    if (!nomPrenomContactUrgence.trim()) errors.push("Nom et prénom du contact en cas d'urgence");
    if (!adresseContact.trim()) errors.push("Adresse du contact");
    if (!codePostalContact.trim()) errors.push("Code postal du contact");
    if (!villeContact.trim()) errors.push("Ville du contact");
    if (!telephoneContact.trim()) errors.push("Téléphone du contact");

    // Si c'est un visa affaire
    if (title.toLowerCase().includes('affaire')) {
      // Fichier invitation obligatoire
      if (!letterInvitationFile) {
        errors.push("Lettre d'invitation");
      }
      // Les infos société en Inde
      if (!nomSteInde.trim()) errors.push("Nom de la sté en Inde");
      if (!adresseSteInde.trim()) errors.push("Adresse de la sté en Inde");
      if (!codePostalSteInde.trim()) errors.push("Code postal de la sté en Inde");
      if (!villeSteInde.trim()) errors.push("Ville de la sté en Inde");
      if (!telephoneSteInde.trim()) errors.push("Téléphone de la sté en Inde");
    }

    // Si c'est un visa tourisme
    if (title.toLowerCase().includes('tourisme')) {
      if (!nomHotel.trim()) errors.push("Nom de l'hôtel");
      if (!adresseHotel.trim()) errors.push("Adresse de l'hôtel");
    }

    // Assurance
    if (!assuranceVisa.trim()) errors.push('Assurance Visa');
    if (!sliderValue) errors.push("Engagement sur les conditions générales de vente");

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  // Revalidation à chaque changement de champs
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateArriveeInde,
    dateSortie,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
    lieuArrivee,
    nomEmployeur,
    adresseEmployeur,
    codePostalEmployeur,
    villeEmployeur,
    telephoneEmployeur,
    nomSteInde,
    adresseSteInde,
    codePostalSteInde,
    villeSteInde,
    telephoneSteInde,
    nomPrenomContactUrgence,
    adresseContact,
    codePostalContact,
    villeContact,
    telephoneContact,
    nomHotel,
    adresseHotel,
    letterInvitationFile,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        // Sélection automatique s'il n'y a qu'un seul voyageur
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l'onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(
        'Les informations saisies ne sont pas enregistrées. Êtes-vous sûr de quitter ce formulaire ?'
      )
    ) {
      navigate(-1);
    }
  };

  // Formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      // Construction du FormData
      const formData = new FormData();
      // On y append tous les champs texte
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateArriveeInde', dateArriveeInde);
      formData.append('dateSortie', dateSortie);
      formData.append('lieuArrivee', lieuArrivee);
      formData.append('nomEmployeur', nomEmployeur);
      formData.append('adresseEmployeur', adresseEmployeur);
      formData.append('codePostalEmployeur', codePostalEmployeur);
      formData.append('villeEmployeur', villeEmployeur);
      formData.append('telephoneEmployeur', telephoneEmployeur);
      formData.append('nomPrenomContactUrgence', nomPrenomContactUrgence);
      formData.append('adresseContact', adresseContact);
      formData.append('codePostalContact', codePostalContact);
      formData.append('villeContact', villeContact);
      formData.append('telephoneContact', telephoneContact);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);

      // Si visa affaire => société Inde + lettre d'invitation
      if (title.toLowerCase().includes('affaire')) {
        formData.append('nomSteInde', nomSteInde);
        formData.append('adresseSteInde', adresseSteInde);
        formData.append('codePostalSteInde', codePostalSteInde);
        formData.append('villeSteInde', villeSteInde);
        formData.append('telephoneSteInde', telephoneSteInde);

        // Ajout du fichier
        if (letterInvitationFile) {
          formData.append('lettreInvitation', letterInvitationFile);
        }
      }

      // Si visa tourisme => infos hôtel
      if (title.toLowerCase().includes('tourisme')) {
        formData.append('nomHotel', nomHotel);
        formData.append('adresseHotel', adresseHotel);
      }

      // Envoi vers le backend (à adapter selon votre URL)
      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateArriveeInde('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setLieuArrivee('');
    setNomEmployeur('');
    setAdresseEmployeur('');
    setCodePostalEmployeur('');
    setVilleEmployeur('');
    setTelephoneEmployeur('');
    setNomSteInde('');
    setAdresseSteInde('');
    setCodePostalSteInde('');
    setVilleSteInde('');
    setTelephoneSteInde('');
    setNomPrenomContactUrgence('');
    setAdresseContact('');
    setCodePostalContact('');
    setVilleContact('');
    setTelephoneContact('');
    setNomHotel('');
    setAdresseHotel('');
    setLetterInvitationFile(null);
  };

  // Juste une fonction vide pour le onClick si disabled
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              Demande enregistree avec succes !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Votre demande de visa pour {title} a bien ete enregistree.
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société et hors Legrand) */}
              {isClientSociete === false && !hideFeesForLegrand && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  Payer en ligne
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                Creer une nouvelle demande
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                Voir mes demandes
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 3,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              Formulaire pour {title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              Retour
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">Sélectionner un voyageur</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label="Sélectionner un voyageur"
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? ` - Passeport: ${v.numeropax}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  Créer un nouveau voyageur
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt="Avatar"
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label="Date de départ de votre voyage (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date d'arrivée en Inde (si différente de date de départ)"
            placeholder="jj/mm/aaaa"
            value={dateArriveeInde}
            onChange={(e) => setDateArriveeInde(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date de retour de votre voyage (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Upload du fichier lettre d'invitation (si visa affaire) */}
          {title.toLowerCase().includes('affaire') && (
            <FileDropZone
              label="Lettre d'invitation"
              file={letterInvitationFile}
              onFileSelected={setLetterInvitationFile}
              required
            />
          )}

          <TextField
            label="Lieu d'arrivée"
            placeholder="Lieu d'arrivée"
            value={lieuArrivee}
            onChange={(e) => setLieuArrivee(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Section Employeur */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Informations de l'employeur
          </Typography>

          <TextField
            label="Nom de l'employeur"
            placeholder="Nom de l'employeur"
            value={nomEmployeur}
            onChange={(e) => setNomEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Adresse de l'employeur"
            placeholder="Adresse de l'employeur"
            value={adresseEmployeur}
            onChange={(e) => setAdresseEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Code postal de l'employeur"
            placeholder="Code postal de l'employeur"
            value={codePostalEmployeur}
            onChange={(e) => setCodePostalEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Ville de l'employeur"
            placeholder="Ville de l'employeur"
            value={villeEmployeur}
            onChange={(e) => setVilleEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Téléphone de l'employeur"
            placeholder="Téléphone de l'employeur"
            value={telephoneEmployeur}
            onChange={(e) => setTelephoneEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Champs société en Inde (si visa affaire) */}
          {title.toLowerCase().includes('affaire') && (
            <>
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Informations de la société en Inde
              </Typography>

              <TextField
                label="Nom de la sté en Inde"
                placeholder="Nom de la société en Inde"
                value={nomSteInde}
                onChange={(e) => setNomSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Adresse de la sté en Inde"
                placeholder="Adresse de la société en Inde"
                value={adresseSteInde}
                onChange={(e) => setAdresseSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Code postal de la sté en Inde"
                placeholder="Code postal de la société en Inde"
                value={codePostalSteInde}
                onChange={(e) => setCodePostalSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Ville de la sté en Inde"
                placeholder="Ville de la société en Inde"
                value={villeSteInde}
                onChange={(e) => setVilleSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Téléphone de la sté en Inde"
                placeholder="Téléphone de la société en Inde"
                value={telephoneSteInde}
                onChange={(e) => setTelephoneSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          )}

          {/* Champs hôtel (si visa tourisme) */}
          {title.toLowerCase().includes('tourisme') && (
            <>
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Informations de l'hôtel
              </Typography>

              <TextField
                label="Nom de l'hôtel"
                placeholder="Nom de l'hôtel"
                value={nomHotel}
                onChange={(e) => setNomHotel(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Adresse de l'hôtel"
                placeholder="Adresse de l'hôtel"
                value={adresseHotel}
                onChange={(e) => setAdresseHotel(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          )}

          {/* Section Contact d'urgence */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Contact en cas d'urgence
          </Typography>

          <TextField
            label="Nom et prénom du contact en cas d'urgence "
            placeholder="Nom et prénom du contact"
            value={nomPrenomContactUrgence}
            onChange={(e) => setNomPrenomContactUrgence(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Adresse du contact"
            placeholder="Adresse du contact"
            value={adresseContact}
            onChange={(e) => setAdresseContact(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Code postal du contact"
            placeholder="Code postal du contact"
            value={codePostalContact}
            onChange={(e) => setCodePostalContact(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Ville du contact"
            placeholder="Ville du contact"
            value={villeContact}
            onChange={(e) => setVilleContact(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Téléphone du contact"
            placeholder="Téléphone du contact"
            value={telephoneContact}
            onChange={(e) => setTelephoneContact(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Module Assurance */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFeesForLegrand && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">Frais de visa : {prixconsulat}€</Typography>
              <Typography variant="body1">Frais de service : {prixintervention}€</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total : {total}€
              </Typography>
            </Box>
          )}

          {/* État du formulaire (incomplet / complet) */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  Formulaire incomplet.
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">Formulaire complet</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                Valider ma demande
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormInde;